import type {
  AdminChildResponse,
  AdminParentResponse,
  AppDailyUsage,
  AppDailyUsageSummaryResponse,
  AppUsage,
  CategoryUsage,
  Child,
  ChildCommandResult,
  ChildDevice,
  ChildDeviceResponse,
  ChildGuardian,
  ChildGuardianResponse,
  ChildLatestLocationsResponse,
  CountByName,
  DailyUsage,
  DailyUsageSummaryResponse,
  Dashboard,
  DashboardAppUsageResponse,
  DashboardCategoryUsageResponse,
  DashboardCountByNameResponse,
  DashboardDevices,
  DashboardDevicesPanel,
  DashboardHealth,
  DashboardHealthPanel,
  DashboardResponse,
  DashboardTrendPoint,
  DashboardTrendPointResponse,
  DashboardTrends,
  DashboardTrendsResponse,
  DashboardUsage,
  DashboardUsagePanel,
  DashboardUsers,
  DashboardUsersPanel,
  DeviceCommand,
  DeviceCommandResponse,
  DeviceLatestLocationResponse,
  DeviceLocation,
  Parent,
  Permission,
  PermissionResponse,
  Role,
  RoleResponse,
  SendChildCommandResponse,
  User,
  UserResponse,
  UserSession,
  UserSessionResponse,
} from "@repo/types"

import { fullName, list, num, phone, str } from "./normalize"

// These mappers hand the UI the domain shapes from @repo/types; see ./normalize
// for why every field goes through a helper.

export function toUser(dto: UserResponse): User {
  const firstName = str(dto.firstName)
  const lastName = str(dto.lastName)
  const email = str(dto.email)

  return {
    id: str(dto.id),
    userType: dto.userType ?? "Admin",
    firstName,
    lastName,
    // Parent/child accounts may carry no name at all; the email local part is a
    // better table cell than an empty one.
    fullName: fullName(firstName, lastName, email.split("@")[0] || phone(dto.phoneNumber)),
    email,
    phoneNumber: phone(dto.phoneNumber),
    lastLoginAt: dto.lastLoginAt ?? null,
    roles: list(dto.roles).map(role => ({ id: str(role.id), name: str(role.name) })),
    permissions: list(dto.permissions),
    createdAt: str(dto.createdAt),
  }
}

export function toChildDevice(dto: ChildDeviceResponse): ChildDevice {
  return {
    id: str(dto.id),
    childProfileId: str(dto.childProfileId),
    platform: dto.platform ?? "Android",
    name: str(dto.name),
    externalDeviceId: str(dto.externalDeviceId),
    manufacturer: str(dto.manufacturer),
    model: str(dto.model),
    osVersion: str(dto.osVersion),
    appVersion: str(dto.appVersion),
    timeZoneId: str(dto.timeZoneId),
    lastSeenAt: dto.lastSeenAt ?? null,
    // Kept nullable: 0% and "never reported" are different states on a battery.
    batteryLevel: dto.batteryLevel ?? null,
    batteryUpdatedAt: dto.batteryUpdatedAt ?? null,
    isBlocked: dto.isBlocked ?? false,
    isLockedOnDevice: dto.isLockedOnDevice ?? false,
    lockedAt: dto.lockedAt ?? null,
    isSetPin: dto.isSetPin ?? false,
    status: dto.status ?? "Active",
    createdAt: str(dto.createdAt),
    updatedAt: dto.updatedAt ?? null,
  }
}

function toChildGuardian(dto: ChildGuardianResponse): ChildGuardian {
  return {
    id: str(dto.id),
    childProfileId: str(dto.childProfileId),
    userId: str(dto.userId),
    role: dto.role ?? "Guardian",
    grantedAt: str(dto.grantedAt),
    revokedAt: dto.revokedAt ?? null,
    isActive: dto.isActive ?? false,
  }
}

export function toChild(dto: AdminChildResponse): Child {
  const firstName = str(dto.firstName)
  const lastName = str(dto.lastName)

  return {
    id: str(dto.id),
    parentUserId: str(dto.parentUserId),
    parentName: str(dto.parentName),
    parentPhoneNumber: phone(dto.parentPhoneNumber),
    childUserId: str(dto.childUserId),
    firstName,
    lastName,
    // A child profile can be created with no name yet; the phone number is the
    // only other thing an admin can recognise the row by.
    fullName: fullName(firstName, lastName, phone(dto.phoneNumber)),
    phoneNumber: phone(dto.phoneNumber),
    birthDate: dto.birthDate ?? null,
    gender: dto.gender ?? null,
    status: dto.status ?? "Active",
    deviceCount: num(dto.deviceCount),
    lastSeenAt: dto.lastSeenAt ?? null,
    isOnline: dto.isOnline ?? false,
    createdAt: str(dto.createdAt),
    updatedAt: dto.updatedAt ?? null,
    devices: list(dto.devices).map(toChildDevice),
    guardians: list(dto.guardians).map(toChildGuardian),
  }
}

export function toParent(dto: AdminParentResponse): Parent {
  const firstName = str(dto.firstName)
  const lastName = str(dto.lastName)
  const email = str(dto.email)

  return {
    id: str(dto.id),
    firstName,
    lastName,
    fullName: fullName(firstName, lastName, email.split("@")[0] || phone(dto.phoneNumber)),
    phoneNumber: phone(dto.phoneNumber),
    email,
    gender: dto.gender ?? null,
    isPhoneNumberVerified: dto.isPhoneNumberVerified ?? false,
    lastLoginAt: dto.lastLoginAt ?? null,
    childCount: num(dto.childCount),
    createdAt: str(dto.createdAt),
    children: list(dto.children).map(toChild),
  }
}

/**
 * A device with no fix yet still comes back in the list, with a null `location`.
 * Those rows carry nothing to show on a map, so they are dropped here rather
 * than leaving every caller to null-check a coordinate pair.
 */
export function toDeviceLocations(dto: ChildLatestLocationsResponse): DeviceLocation[] {
  return list(dto.devices)
    .filter((device): device is DeviceLatestLocationResponse & { location: NonNullable<DeviceLatestLocationResponse["location"]> } =>
      Boolean(device.location))
    .map(device => ({
      childDeviceId: str(device.childDeviceId),
      deviceName: str(device.deviceName),
      status: device.status ?? "Active",
      lastSeenAt: device.lastSeenAt ?? null,
      batteryLevel: device.batteryLevel ?? null,
      latitude: num(device.location.latitude),
      longitude: num(device.location.longitude),
      accuracyMeters: device.location.accuracyMeters ?? null,
      recordedAt: str(device.location.recordedAt),
      isMocked: device.location.isMocked ?? false,
      source: str(device.location.source),
    }))
}

export function toDailyUsage(dto: DailyUsageSummaryResponse): DailyUsage {
  return {
    usageDate: str(dto.usageDate),
    totalScreenTimeMinutes: num(dto.totalScreenTimeMinutes),
    pickupCount: num(dto.pickupCount),
    blockedAttemptCount: num(dto.blockedAttemptCount),
  }
}

export function toAppDailyUsage(dto: AppDailyUsageSummaryResponse): AppDailyUsage {
  return {
    usageDate: str(dto.usageDate),
    packageName: str(dto.packageName),
    // Catalogue entries without a display name fall back to the package id.
    displayName: str(dto.displayName) || str(dto.packageName),
    totalUsageMinutes: num(dto.totalUsageMinutes),
    openCount: num(dto.openCount),
    lastUsedAt: dto.lastUsedAt ?? null,
  }
}

export function toDeviceCommand(dto: DeviceCommandResponse): DeviceCommand {
  return {
    id: str(dto.id),
    childDeviceId: str(dto.childDeviceId),
    commandType: str(dto.commandType),
    status: dto.status ?? "Pending",
    requestedAt: str(dto.requestedAt),
    failureReason: str(dto.failureReason),
  }
}

export function toChildCommandResult(dto: SendChildCommandResponse): ChildCommandResult {
  return {
    deliveredCount: num(dto.deliveredCount),
    pendingCount: num(dto.pendingCount),
    commands: list(dto.commands).map(toDeviceCommand),
  }
}

export function toRole(dto: RoleResponse): Role {
  return {
    id: str(dto.id),
    name: str(dto.name),
    description: str(dto.description),
    isSystem: dto.isSystem ?? false,
    permissions: list(dto.permissions),
    createdAt: str(dto.createdAt),
    updatedAt: dto.updatedAt ?? null,
  }
}

export function toPermission(dto: PermissionResponse): Permission {
  return {
    id: str(dto.id),
    code: str(dto.code),
    module: str(dto.module),
    moduleDisplayName: str(dto.moduleDisplayName) || str(dto.module),
    description: str(dto.description),
    isCritical: dto.isCritical ?? false,
    isReadOnly: dto.isReadOnly ?? false,
  }
}

function toCountByName(dto: DashboardCountByNameResponse): CountByName {
  return { name: str(dto.name), count: num(dto.count) }
}

function toCategoryUsage(dto: DashboardCategoryUsageResponse): CategoryUsage {
  return { category: str(dto.category), totalMinutes: num(dto.totalMinutes) }
}

function toAppUsage(dto: DashboardAppUsageResponse): AppUsage {
  return {
    packageName: str(dto.packageName),
    // Catalogue entries without a display name fall back to the package id.
    displayName: str(dto.displayName) || str(dto.packageName),
    totalMinutes: num(dto.totalMinutes),
    deviceCount: num(dto.deviceCount),
  }
}

function toDashboardUsers(dto: DashboardUsersPanel | undefined): DashboardUsers {
  return {
    totalParents: num(dto?.totalParents),
    totalChildren: num(dto?.totalChildren),
    totalAdmins: num(dto?.totalAdmins),
    activeChildProfiles: num(dto?.activeChildProfiles),
    newUsersToday: num(dto?.newUsersToday),
    newUsersLast7Days: num(dto?.newUsersLast7Days),
    newUsersLast30Days: num(dto?.newUsersLast30Days),
    activeUsersLast30Days: num(dto?.activeUsersLast30Days),
    incompleteParentSignups: num(dto?.incompleteParentSignups),
    incompleteChildSignups: num(dto?.incompleteChildSignups),
    parentsByPlatform: list(dto?.parentsByPlatform).map(toCountByName),
    childrenByPlatform: list(dto?.childrenByPlatform).map(toCountByName),
  }
}

function toDashboardDevices(dto: DashboardDevicesPanel | undefined): DashboardDevices {
  return {
    totalDevices: num(dto?.totalDevices),
    activeDevices: num(dto?.activeDevices),
    disabledDevices: num(dto?.disabledDevices),
    onlineNow: num(dto?.onlineNow),
    seenLast24Hours: num(dto?.seenLast24Hours),
    blockedDevices: num(dto?.blockedDevices),
    lockedDevices: num(dto?.lockedDevices),
    byPlatform: list(dto?.byPlatform).map(toCountByName),
    byAppVersion: list(dto?.byAppVersion).map(toCountByName),
  }
}

function toDashboardUsage(dto: DashboardUsagePanel | undefined): DashboardUsage {
  return {
    totalScreenTimeMinutesToday: num(dto?.totalScreenTimeMinutesToday),
    totalScreenTimeMinutesLast7Days: num(dto?.totalScreenTimeMinutesLast7Days),
    averageDailyMinutesPerChild: num(dto?.averageDailyMinutesPerChild),
    childrenWithUsageToday: num(dto?.childrenWithUsageToday),
    topApps: list(dto?.topApps).map(toAppUsage),
    topCategories: list(dto?.topCategories).map(toCategoryUsage),
  }
}

function toDashboardHealth(dto: DashboardHealthPanel | undefined): DashboardHealth {
  return {
    commandsByStatus: list(dto?.commandsByStatus).map(toCountByName),
    commandsLast24Hours: num(dto?.commandsLast24Hours),
    pendingCommands: num(dto?.pendingCommands),
    pendingCommandsOlderThanDay: num(dto?.pendingCommandsOlderThanDay),
    pairingCompletionRatePercent: num(dto?.pairingCompletionRatePercent),
    pairingSessionsLast7Days: num(dto?.pairingSessionsLast7Days),
    failedJobRunsLast24Hours: num(dto?.failedJobRunsLast24Hours),
    succeededJobRunsLast24Hours: num(dto?.succeededJobRunsLast24Hours),
    lastJobRunAt: dto?.lastJobRunAt ?? null,
    unreadNotifications: num(dto?.unreadNotifications),
    notificationsLast24Hours: num(dto?.notificationsLast24Hours),
  }
}

export function toDashboard(dto: DashboardResponse): Dashboard {
  return {
    users: toDashboardUsers(dto.users),
    devices: toDashboardDevices(dto.devices),
    usage: toDashboardUsage(dto.usage),
    health: toDashboardHealth(dto.health),
    generatedAt: str(dto.generatedAt),
  }
}

function toTrendPoint(dto: DashboardTrendPointResponse): DashboardTrendPoint {
  return {
    date: str(dto.date),
    newUsers: num(dto.newUsers),
    newDevices: num(dto.newDevices),
    screenTimeMinutes: num(dto.screenTimeMinutes),
    activeChildren: num(dto.activeChildren),
  }
}

export function toDashboardTrends(dto: DashboardTrendsResponse): DashboardTrends {
  return {
    from: str(dto.from),
    to: str(dto.to),
    points: list(dto.points).map(toTrendPoint),
  }
}

export function toUserSession(dto: UserSessionResponse): UserSession {
  return {
    id: str(dto.id),
    deviceName: str(dto.deviceName),
    externalDeviceId: str(dto.externalDeviceId),
    createdAt: str(dto.createdAt),
    expiresAt: str(dto.expiresAt),
  }
}
